/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import { ChevronDown, ChevronUp, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "@/components/site/SEO";

const FAQS = [
  {
    section: "Getting started",
    items: [
      ["Is OneDial Africa free to join?", "Yes. Creating an account, verifying your email and managing your profile is free forever. You only pay for what you use — calls, top-ups and any service you choose."],
      ["What countries can I call?", "Over 50 destinations across Africa, plus the UK, EU, US, Canada and most major countries worldwide. See our Rates page for the full list."],
      ["Which devices does it work on?", "Today we work on any modern web browser. Native iOS and Android apps are coming in the next release."],
    ],
  },
  {
    section: "Wallet & payments",
    items: [
      ["How does the wallet work?", "Your OneDial wallet holds GBP service credit. Top up by card (Stripe), then use the balance to make calls or buy other services. Funds never expire."],
      ["Is my money safe?", "Yes. Card payments are processed by Stripe (FCA-regulated). Your card details never touch our servers. Your wallet balance is held in a UK bank in a safeguarded account."],
      ["Can I get a refund?", "Top-ups are generally non-refundable, but we'll work with you in exceptional cases. Contact support via the in-app ticket system."],
      ["What payment methods do you accept?", "All major UK and international debit/credit cards, Apple Pay and Google Pay via Stripe Checkout. Mobile money for African senders is coming in v1.2."],
    ],
  },
  {
    section: "Calling",
    items: [
      ["How is my call billed?", "Per-second from the moment your call is answered. There is no connection fee. Look at the Rates page to see the per-minute price for each destination."],
      ["What happens if my balance runs out?", "We'll notify you when your balance falls below £1. Calls automatically end when the balance reaches zero — no surprise charges."],
      ["Why is my call price higher than I expected?", "Some operators (especially mobile networks in certain countries) charge a premium. The price you see when dialling on our app is always the final price."],
      ["Can I record calls?", "Not at this time. We respect privacy laws across all jurisdictions; recording is on the roadmap with explicit consent flows."],
    ],
  },
  {
    section: "Account & security",
    items: [
      ["How do I delete my account?", <>Visit <Link to="/account-deletion" className="text-brand-greenDark underline">our deletion request page</Link>. We'll process your request within 30 days and remove your personal data in accordance with GDPR / UK GDPR.</>],
      ["How do I export my data?", "Logged-in customers can download a complete JSON export of their account data from the Profile page (GDPR right to portability)."],
      ["I forgot my password — what now?", "Use the 'Forgot password' link on the login page. We'll email you a single-use reset link valid for 60 minutes."],
      ["Is two-factor authentication available?", "We send an email OTP for sensitive actions. App-based 2FA (TOTP) is coming in v1.2."],
    ],
  },
  {
    section: "Referrals & rewards",
    items: [
      ["How do referrals work?", "When you refer a friend, both of you get £2 in your wallet the moment they make their first £10 top-up. Find your unique link inside your dashboard."],
      ["Are there promo codes?", "Yes — keep an eye on our email newsletter and the in-app notifications centre. Codes apply automatically when redeemed."],
    ],
  },
];

export default function FAQ() {
  return (
    <div className="bg-brand-bgSoft text-brand-ink" data-testid="faq-page">
      <SEO title="Frequently Asked Questions" path="/faq"
        description="Answers to common questions about OneDial Africa — calling, wallet, payments, security and account management." />

      <section className="bg-brand-ink text-white">
        <div className="container-page py-16 lg:py-20">
          <p className="label-eyebrow text-brand-green">Help centre</p>
          <h1 className="mt-3 font-display font-black text-4xl sm:text-5xl lg:text-6xl">FAQs</h1>
          <p className="mt-4 text-white/70 max-w-2xl">Answers to the questions our diaspora community asks most. If you can't find what you're looking for, our team is one tap away.</p>
        </div>
      </section>

      <section className="container-page py-12 space-y-10">
        {FAQS.map((sec, sIdx) => (
          <div key={sec.section}>
            <h2 className="font-display font-extrabold text-2xl mb-4">{sec.section}</h2>
            <div className="divide-y divide-brand-line rounded-2xl border border-brand-line bg-white">
              {sec.items.map(([q, a], i) => (
                <FAQRow key={q} q={q} a={a} testid={`faq-${sIdx}-${i}`} />
              ))}
            </div>
          </div>
        ))}

        <div className="rounded-2xl border border-brand-line bg-white p-6 flex flex-wrap items-center gap-4">
          <MessageCircle className="h-6 w-6 text-brand-greenDark" />
          <div className="flex-1 min-w-[200px]">
            <p className="font-semibold">Didn't find your answer?</p>
            <p className="text-sm text-brand-muted">Open a support ticket from your dashboard or message us via the WhatsApp chat button.</p>
          </div>
          <Link to="/app/support" data-testid="faq-cta-support" className="rounded-full bg-brand-green hover:bg-brand-greenHover text-white px-5 py-2.5 text-sm font-semibold">Open a ticket</Link>
        </div>
      </section>
    </div>
  );
}

function FAQRow({ q, a, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <button type="button" onClick={() => setOpen(!open)} data-testid={testid}
      className="w-full text-left px-5 py-4 hover:bg-brand-bgSoft transition">
      <div className="flex items-start gap-3">
        <span className="flex-1 font-semibold">{q}</span>
        {open ? <ChevronUp className="h-4 w-4 mt-1 text-brand-muted" /> : <ChevronDown className="h-4 w-4 mt-1 text-brand-muted" />}
      </div>
      {open && <div className="mt-3 text-sm text-brand-ink/80 leading-relaxed">{a}</div>}
    </button>
  );
}
